import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid2 as Grid, Stack} from 'views/components';

import FilterBar from './scene/filter/FilterBar';
import Header from './scene/Header';
import List from './scene/list/List';
import {generateFilterOptions} from './scene/filter/FilterGenerator';

import backend from 'utils/backend';

// This list offers Loom-only statuses such as UNRESPONSIVE, so it keeps its filter under its own
// session key. A filter the legacy Leads list does not offer must not carry over to it.
const FILTER_STORAGE_KEY = 'LOOM_LEAD_FILTER';

const filterOptions = generateFilterOptions();

const defaultSelection = () => {
  return {
    order: {orderBy: 'creationTime', orderDirc: 'DESC'},
    filter: {
      status: {
        filterData: filterOptions['status'],
        optionSelected: 'ALL',
      },
    },
  };
};

// Leads read only from Loom and keyed by their Loom client id. The list writes nothing.
const LoomLeadList = (props) => {
  const [reload, setReload] = React.useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());
  const [visibleLeads, setVisibleLeads] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [stage, setStage] = React.useState('FILL');
  const [selection, setSelection] = React.useState(() => {
    const storedSelection = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return storedSelection ? JSON.parse(storedSelection) : defaultSelection();
  });

  const progressWrap = props.statusProgressWrap;

  const requestIdRef = React.useRef(0);

  const isListLoading = stage === 'LOADING';

  React.useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(selection));
  }, [selection]);

  const loadData = React.useCallback(async (reqId = requestIdRef.current, startKey = lastEvaluatedKey) => {
    setStage('LOADING');

    await progressWrap(async () => {
      const filter = [];

      for (const filterSelected of Object.values(selection.filter)) {
        if (!filterSelected.optionSelected || filterSelected.optionSelected === 'ALL') {
          continue;
        }

        if (Array.isArray(filterSelected.optionSelected) && filterSelected.optionSelected.length === 0) {
          continue;
        }

        const rawValue = filterSelected.optionSelected;
        const value = filterSelected.filterData.type === 'text' && typeof rawValue === 'string' ?
          rawValue.replace(/[^a-z0-9 ]/gi, '') : rawValue;
        filter.push(
            {
              key: filterSelected.filterData.key,
              value: value,
            },
        );
      }

      const sortBy = {
        sortKey: 'createdAt',
        sortDirection: 'DESC',
      };

      const result = await backend.loom.loomLead.queryClient(filter, sortBy, startKey);

      if (reqId !== requestIdRef.current) return;

      let filteredList = result.list;

      // Filter out ACTIVE and INACTIVE when status is ALL
      const statusFilter = Object.values(selection.filter).find((e) => e.filterData.key === 'status');
      if (statusFilter && (statusFilter.optionSelected === 'ALL' || !statusFilter.optionSelected)) {
        filteredList = filteredList.filter((item) => item.status !== 'ACTIVE' && item.status !== 'INACTIVE');
      }

      setVisibleLeads((e) => {
        return [
          ...e,
          ...filteredList,
        ];
      });
      setLastEvaluatedKey(result.lastEvaluatedKey);
    }, {showProgress: false});

    if (reqId === requestIdRef.current) {
      setStage('COMPLETE');
    }
  }, [lastEvaluatedKey, progressWrap, selection]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setVisibleLeads([]);
    setLastEvaluatedKey(null);
    loadData(requestIdRef.current, null);
  }, [selection, reload]);

  const updateFilter = (filter, option) => {
    setSelection((e) => {
      if (option) {
        return {
          ...e,
          filter: {
            ...e.filter,
            [filter.key]: {
              filterData: filter,
              optionSelected: option.key,
              label: option?.label,
            },
          },
        };
      } else {
        const newFilter = {...e.filter};
        delete newFilter[filter.key];
        return {
          ...e,
          filter: newFilter,
        };
      }
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Header
          lastUpdatedAt={lastUpdatedAt}
          numRefresh={reload}
          setLastUpdatedAt={setLastUpdatedAt}
          setNumRefresh={setReload}
        />
      </Grid>
      <Grid size={12}>
        <Stack spacing={4} width={1} pb={{xs: 0, md: 2}}>
          <FilterBar
            selection={selection}
            filterOptions={filterOptions}
            reload={reload}
            updateFilter={updateFilter}
          />
          <List
            data={visibleLeads}
            isLoading={isListLoading}
            loadMore={loadData}
            hasMore={!!lastEvaluatedKey}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
LoomLeadList.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(LoomLeadList));
